import React, { useEffect } from 'react';
import styled from 'styled-components';

import { GqlProductDemandCount, useProductDemandCountsQuery } from 'types/graphql';

import CartItemClass from 'src/state/cart-item';

import { useProductUrgencyEnabled } from 'src/hooks/use-product-urgency-enabled';
import useCart from 'src/hooks/use-cart';

import { FlameIcon } from 'src/assets/flame-icon';

type ProductDemandProps = {
  item: CartItemClass;
  shouldShowDemandCount: boolean;
  onProductDemandChange?: (productId: string, productDemandCount: GqlProductDemandCount | undefined) => void;
};

export const ProductDemand = ({
  item,
  shouldShowDemandCount,
  onProductDemandChange,
}: ProductDemandProps): JSX.Element | null => {
  const { dispensary } = useCart();
  const isProductDemandEnabled = useProductUrgencyEnabled();
  const itemId = item.id;

  const { data, loading } = useProductDemandCountsQuery({
    variables: {
      dispensaryId: dispensary?.id,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      productIds: [itemId ?? ''],
    },
    fetchPolicy: 'network-only',
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    skip: !dispensary?.id || !itemId || !isProductDemandEnabled,
  });

  const productDemandCount = data?.getProductDemandCounts?.[0];

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!loading && productDemandCount && itemId && onProductDemandChange) {
      onProductDemandChange(itemId, productDemandCount);
    }
  }, [productDemandCount, onProductDemandChange, itemId, loading]);

  if (!shouldShowDemandCount || !productDemandCount) {
    return null;
  }

  return (
    <Wrapper data-testid='product-demand'>
      <StyledFlameIcon height={12} width={12} />
      <Content>Going fast! {productDemandCount.count} others have this in their cart.</Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
  line-height: 1;
  margin-top: 16px;
`;

const StyledFlameIcon = styled(FlameIcon)`
  flex: 0 0 auto;
`;

const Content = styled.p`
  flex: 1;
`;
